import { ChatProvider } from '@/domain/account';
import type { ChatConfig, SupportedChatProvider } from '@/domain/chat';
import {
  clearHideRetries,
  getChatMobileMode,
  isChatPanelOpen,
  registerActiveHideLauncher,
  resetChatSession,
  setChatPanelOpen,
  setSessionMobileMode,
} from './chat-session';
import { comm100Adapter } from './providers/comm100';
import { livechatAdapter } from './providers/livechat';
import { tawkAdapter } from './providers/tawk';
import type { ChatAdapter, ChatLoadContext } from './providers/types';
import { zendeskAdapter } from './providers/zendesk';
import { setChatUnreadCount } from './unread-store';

const adapters: Record<SupportedChatProvider, ChatAdapter> = {
  [ChatProvider.Zendesk]: zendeskAdapter,
  [ChatProvider.LiveChat]: livechatAdapter,
  [ChatProvider.Tawk]: tawkAdapter,
  [ChatProvider.Comm100]: comm100Adapter,
};

let active: ChatAdapter | null = null;
let loadPromise: Promise<void> | null = null;
let loadedKey: string | null = null;
let adapterOverrides: Partial<Record<SupportedChatProvider, ChatAdapter>> | null = null;
/** Footer / NeedHelp may fire before the vendor script finishes loading. */
let pendingOpen = false;

export function selectChatAdapter(provider: SupportedChatProvider): ChatAdapter {
  return adapterOverrides?.[provider] ?? adapters[provider];
}

export function getActiveChatAdapter(): ChatAdapter | null {
  return active;
}

export function isChatReady(): boolean {
  return active !== null && loadPromise === null;
}

function bindActive(adapter: ChatAdapter | null): void {
  active = adapter;
  registerActiveHideLauncher(adapter ? () => adapter.hideLauncher() : null);
}

export async function loadChatWidget(
  config: ChatConfig,
  ctx: Omit<ChatLoadContext, 'key' | 'onUnread'>,
): Promise<void> {
  const adapter = selectChatAdapter(config.provider);
  const loadKey = `${config.provider}:${config.key}`;
  setSessionMobileMode(ctx.isMobile);

  if (loadedKey === loadKey && active === adapter) {
    adapter.identify?.(ctx.username);
    // Do not re-hide while the visitor has the panel open (account refetch).
    if (ctx.isMobile && !isChatPanelOpen()) {
      adapter.hideLauncher();
    }
    if (pendingOpen) {
      pendingOpen = false;
      doOpen(adapter);
    }
    return;
  }

  loadedKey = loadKey;
  bindActive(adapter);
  setChatPanelOpen(false);

  const fullCtx: ChatLoadContext = {
    key: config.key,
    language: ctx.language,
    username: ctx.username,
    isMobile: ctx.isMobile,
    onUnread: setChatUnreadCount,
  };

  loadPromise = adapter
    .load(fullCtx)
    .then(() => {
      if (getChatMobileMode() && !isChatPanelOpen()) {
        adapter.hideLauncher();
      }
      if (pendingOpen) {
        pendingOpen = false;
        doOpen(adapter);
      }
    })
    .finally(() => {
      loadPromise = null;
    });
  await loadPromise;
}

/**
 * Keep vendor floating buttons hidden while the mobile footer owns open.
 * Called on load and whenever the viewport crosses the lg breakpoint.
 */
export function setChatMobileMode(isMobile: boolean): void {
  setSessionMobileMode(isMobile);
  if (isMobile && !isChatPanelOpen()) {
    active?.hideLauncher();
  }
}

function doOpen(adapter: ChatAdapter): void {
  clearHideRetries();
  setChatPanelOpen(true);
  adapter.open();
}

export function openChat(): void {
  if (!active || loadPromise) {
    pendingOpen = true;
    return;
  }
  doOpen(active);
}

export function identifyChatUser(username: string | null): void {
  active?.identify?.(username);
}

/** Test helper — clears module state between tests. */
export function resetChatController(): void {
  resetChatSession();
  active = null;
  loadPromise = null;
  loadedKey = null;
  adapterOverrides = null;
  pendingOpen = false;
}

/** Test helper — swap adapters without loading real vendor scripts. */
export function __setAdapterOverridesForTests(
  overrides: Partial<Record<SupportedChatProvider, ChatAdapter>> | null,
): void {
  adapterOverrides = overrides;
}
